/**
 * Черновик FAQ из вопросов клиентов — вкладка «Сайт и контент» раздела «Голос
 * клиента» (#843; спека docs/plans/2026-10-02-customer-questions-and-decline-reasons.md
 * §8 п. 6 и §9 «FAQ1–FAQ3»).
 *
 * Три контракта одного нажатия:
 *
 * 1. `voiceFaqDraftRequestSchema` — тело `POST /analytics/voice/faq-draft`
 *    (ui-worker → edge-api): то же окно и тот же фильтр, что у раздела.
 * 2. `voiceFaqDraftServiceRequestSchema` — edge-api → workflows-api: частые
 *    вопросы окна, уже прочитанные в области читателя и ограниченные
 *    (`VOICE_FAQ_MAX_*`). Платный вызов живёт в workflows-api, как у проверки
 *    формулировки критерия (#861): одна дверь для платного пути.
 * 3. `voiceFaqDraftModelOutputSchema` — разбор ответа модели;
 *    `voiceFaqDraftResponseSchema` — что видит клиент.
 *
 * Черновик НЕ хранится (решение владельца 05.10): нет таблицы и нового срока
 * хранения, экран держит его в памяти, повторное нажатие строит заново.
 */
import { z } from 'zod/v4';
import { projectIdsFilterSchema } from './common';
import { callCategorySchema } from './records';
import { QUESTION_SUBJECT_CODES } from './voice-extraction';
import {
  voiceThemeFilterCodeSchema,
  voiceThemeFilterEchoSchema,
} from './voice-of-customer';

/** Сколько предметов вопросов уходит в один черновик — все коды набора. */
export const VOICE_FAQ_MAX_SUBJECTS = QUESTION_SUBJECT_CODES.length;
/** Формулировок на предмет: свежие и разные. 10 × 4 = потолок ниже. */
export const VOICE_FAQ_WORDINGS_PER_SUBJECT = 4;
/** Потолок формулировок в одном запросе к модели. */
export const VOICE_FAQ_MAX_WORDINGS = 40;
/**
 * Потолок длины одной формулировки. Шаг пишет их не длиннее 120 знаков
 * (`VOICE_EXTRACTION_PHRASE_MAX_CHARS`); запас — на строки старой версии.
 */
export const VOICE_FAQ_MAX_WORDING_CHARS = 200;
/** Пар «вопрос — ответ» в черновике. */
export const VOICE_FAQ_MAX_ITEMS = 15;
export const VOICE_FAQ_QUESTION_MAX_CHARS = 200;
export const VOICE_FAQ_ANSWER_MAX_CHARS = 600;
/**
 * Черновиков на организацию за скользящие сутки (решение владельца 05.10).
 * Платит платформа, кошелёк клиента не спрашивается и не списывается — потолок
 * и есть граница расходов этой кнопки, как у разбора скрипта и проверки
 * формулировки критерия.
 */
export const VOICE_FAQ_DAILY_LIMIT = 10;
/**
 * Черновиков на одного пользователя за скользящие сутки — внутри суточного
 * лимита организации: один читатель не выбирает его весь.
 */
export const VOICE_FAQ_USER_DAILY_LIMIT = 5;
/**
 * Сколько edge-api ждёт workflows-api: один вызов модели с рассуждением.
 * Клиент ждёт на 10 с дольше, чтобы прочитать ответ edge «слишком долго».
 */
export const VOICE_FAQ_PROXY_DEADLINE_MS = 90_000;

const questionSubjectCodeSchema = z.enum(QUESTION_SUBJECT_CODES);

/** Тело запроса клиента: окно и фильтр раздела. Не `.strict()` — лишнее отбрасывается. */
export const voiceFaqDraftRequestSchema = z.object({
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  /** @deprecated legacy single-project filter — как у `voiceOfCustomerQuerySchema`. */
  projectId: z.string().optional(),
  projectIds: projectIdsFilterSchema,
  callCategory: callCategorySchema.optional(),
  includeDemo: z.enum(['yes', 'no']).optional(),
  /**
   * Черновик по вопросам разговоров одной темы (#2098) — тот же фильтр, что
   * у раздела. Суточный лимит черновика общий на организацию: тема его не
   * делит. Ответ с темой несёт эхо `themeFilter`; без эха (старый edge-api)
   * черновик собран без фильтра.
   */
  themeCode: voiceThemeFilterCodeSchema.optional(),
});

/** Один предмет вопросов окна: «в K разговорах», свежие разные формулировки. */
export const voiceFaqSourceSubjectSchema = z.object({
  subjectCode: questionSubjectCodeSchema,
  calls: z.number().int().positive(),
  wordings: z
    .array(z.string().trim().min(1).max(VOICE_FAQ_MAX_WORDING_CHARS))
    .min(1)
    .max(VOICE_FAQ_WORDINGS_PER_SUBJECT),
});

/** edge-api → workflows-api. Организация и пользователь — из сессии, не от клиента. */
export const voiceFaqDraftServiceRequestSchema = z
  .object({
    orgId: z.string().min(1),
    /** Потолок пользователя. Нет (старый edge-api) — только потолок организации. */
    userId: z.string().min(1).max(200).optional(),
    traceId: z.string().optional(),
    subjects: z.array(voiceFaqSourceSubjectSchema).min(1).max(VOICE_FAQ_MAX_SUBJECTS),
  })
  .refine(
    (body) =>
      body.subjects.reduce((sum, subject) => sum + subject.wordings.length, 0) <=
      VOICE_FAQ_MAX_WORDINGS,
    { message: `At most ${VOICE_FAQ_MAX_WORDINGS} wordings per draft` }
  )
  .refine(
    (body) =>
      new Set(body.subjects.map((subject) => subject.subjectCode)).size ===
      body.subjects.length,
    { message: 'Each subject at most once' }
  );

/**
 * Одна пара в ответе модели — форма без границ длины: разбор
 * (workflows-api, `parseVoiceFaqOutput`) берёт пары по одной, минимизирует
 * ПДн и только потом режет до `VOICE_FAQ_*_MAX_CHARS` — заполнитель вроде
 * `[EMAIL]` не может вывести строку за контракт. Негодная пара отбрасывается,
 * а не роняет весь ответ. Границы длины в самом вызове держит строгая
 * JSON-схема (`maxLength`).
 */
export const voiceFaqDraftModelItemSchema = z.object({
  subject_code: questionSubjectCodeSchema,
  question: z.string(),
  answer_stub: z.string(),
});

export const voiceFaqDraftItemSchema = z.object({
  subjectCode: questionSubjectCodeSchema,
  question: z.string().min(1).max(VOICE_FAQ_QUESTION_MAX_CHARS),
  /** Заготовка ответа: заполнить и проверить клиенту, фактов о бизнесе в ней нет. */
  answerStub: z.string().min(1).max(VOICE_FAQ_ANSWER_MAX_CHARS),
});

/** Сам черновик. `generated: true` — пометка «сгенерировано» на черновике. */
export const voiceFaqDraftSchema = z.object({
  generated: z.literal(true),
  /** Язык организации (`orgs.settings_json.language`, иначе `en`). */
  language: z.string().min(2).max(10),
  items: z.array(voiceFaqDraftItemSchema).min(1).max(VOICE_FAQ_MAX_ITEMS),
  generatedAt: z.string().datetime({ offset: true }),
});

/**
 * Почему черновик не построен — отказ, а не сбой. Отказа по балансу нет:
 * черновик оплачен платформой.
 * - `feature_disabled` — флаг или тариф закрыт;
 * - `over_limit_minute` / `over_limit_hour` / `over_limit_day` — потолок
 *   организации, по ступени, которая сработала;
 * - `over_user_limit` — потолок пользователя (`VOICE_FAQ_USER_DAILY_LIMIT`);
 * - `feature_unavailable` — флаг или тариф не прочитаны (временный сбой D1),
 *   `limit_unavailable` — счётчик недоступен: оба — «попробуйте позже»;
 * - `over_limit` — прежний общий код потолка, оставлен для отката: новый
 *   workflows-api его не шлёт.
 * Новые коды — добавочные: разбор, не знающий кода, увидит расхождение
 * контракта, а не неверный ответ.
 */
export const VOICE_FAQ_DRAFT_REFUSALS = [
  'feature_disabled',
  'over_limit',
  'over_limit_minute',
  'over_limit_hour',
  'over_limit_day',
  'over_user_limit',
  'feature_unavailable',
  'limit_unavailable',
] as const;
export const voiceFaqDraftRefusalSchema = z.enum(VOICE_FAQ_DRAFT_REFUSALS);

/**
 * workflows-api → edge-api. `costUsd` — себестоимость; edge срезает её
 * разбором. `subjects` — сколько предметов дошло до модели после повторной
 * редакции (подпись под черновиком); нет (старый workflows-api) — edge берёт
 * своё число.
 */
export const voiceFaqDraftServiceResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ready'),
    draft: voiceFaqDraftSchema,
    costUsd: z.number().nonnegative(),
    subjects: z.number().int().nonnegative().optional(),
  }),
  z.object({ status: z.literal('refused'), reason: voiceFaqDraftRefusalSchema }),
  /** Модель ответила пустым списком: материала на черновик мало. Добавочный статус. */
  z.object({ status: z.literal('no_draft'), costUsd: z.number().nonnegative() }),
]);

/**
 * edge-api → workflows-api до тяжёлого чтения вопросов: потолки без траты
 * слота (`/peek` счётчика). Окончательный вопрос задаёт сам черновик.
 */
export const voiceFaqDraftPeekRequestSchema = z.object({
  orgId: z.string().min(1),
  userId: z.string().min(1).max(200).optional(),
});

export const voiceFaqDraftPeekResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok') }),
  z.object({ status: z.literal('refused'), reason: voiceFaqDraftRefusalSchema }),
]);

/**
 * Ответ клиенту. `no_questions` — в окне нет вопросов клиентов: модель не
 * звалась, слот лимита не потрачен. `no_usable_wordings` — вопросы в окне
 * есть (`calls` — разговоров с ними), но ни одной формулировки, которую можно
 * отправить: все скрыты редакцией ПДн. Модель не звалась. `calls`
 * необязателен — ответ без него разбирается тем же контрактом.
 * `no_draft` — вопросы были и ушли в модель, но она не нашла в них ни одной
 * пары, которую стоило бы предложить («материала пока мало»): не сбой —
 * «повторить» ничего не изменит, пока вопросов не станет больше; слот лимита
 * потрачен. Статус добавочный: экран, не знающий его, не разберёт ответ, поэтому
 * выпуск идёт workflows-api → edge-api → ui-worker. Отказы и сбои — HTTP-статус
 * с `code` (`VOICE_FAQ_DRAFT_ERROR_CODES`). Тело несло `themeCode` (#2098) —
 * у каждого статуса эхо `themeFilter`: черновик собран по вопросам разговоров
 * темы; эха нет — собран без фильтра.
 */
export const voiceFaqDraftResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ready'),
    draft: voiceFaqDraftSchema,
    /** Сколько предметов и разговоров легло в основу — подпись под черновиком. */
    source: z.object({
      subjects: z.number().int().nonnegative(),
      calls: z.number().int().nonnegative(),
    }),
    themeFilter: voiceThemeFilterEchoSchema.optional(),
  }),
  z.object({
    status: z.literal('no_questions'),
    themeFilter: voiceThemeFilterEchoSchema.optional(),
  }),
  z.object({
    status: z.literal('no_usable_wordings'),
    calls: z.number().int().nonnegative().optional(),
    themeFilter: voiceThemeFilterEchoSchema.optional(),
  }),
  z.object({
    status: z.literal('no_draft'),
    themeFilter: voiceThemeFilterEchoSchema.optional(),
  }),
]);

/**
 * Коды ошибок маршрута — экран переводит их в слова: 403 `tier_not_eligible`
 * / `feature_disabled`; 429 `over_limit_minute` / `over_limit_hour` /
 * `over_limit_day` / `over_user_limit` (и прежний `over_limit`); 503
 * `temporarily_unavailable` / `draft_unavailable` / `draft_timeout`; 500
 * `draft_failed`.
 */
export const VOICE_FAQ_DRAFT_ERROR_CODES = [
  'tier_not_eligible',
  'feature_disabled',
  'over_limit',
  'over_limit_minute',
  'over_limit_hour',
  'over_limit_day',
  'over_user_limit',
  'temporarily_unavailable',
  'draft_unavailable',
  'draft_timeout',
  'draft_failed',
  // Фильтр темы (#2098): 403 — флаг тем закрыт, 400 — темы нет в наборе.
  'themes_disabled',
  'theme_filter_invalid',
] as const;

export type VoiceFaqDraftRequest = z.infer<typeof voiceFaqDraftRequestSchema>;
export type VoiceFaqSourceSubject = z.infer<typeof voiceFaqSourceSubjectSchema>;
export type VoiceFaqDraftServiceRequest = z.infer<
  typeof voiceFaqDraftServiceRequestSchema
>;
export type VoiceFaqDraftModelItem = z.infer<typeof voiceFaqDraftModelItemSchema>;
export type VoiceFaqDraftItem = z.infer<typeof voiceFaqDraftItemSchema>;
export type VoiceFaqDraft = z.infer<typeof voiceFaqDraftSchema>;
export type VoiceFaqDraftRefusal = z.infer<typeof voiceFaqDraftRefusalSchema>;
export type VoiceFaqDraftServiceResponse = z.infer<
  typeof voiceFaqDraftServiceResponseSchema
>;
export type VoiceFaqDraftResponse = z.infer<typeof voiceFaqDraftResponseSchema>;
export type VoiceFaqDraftPeekResponse = z.infer<typeof voiceFaqDraftPeekResponseSchema>;
export type VoiceFaqDraftErrorCode = (typeof VOICE_FAQ_DRAFT_ERROR_CODES)[number];
